import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  Alert,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  Select,
  Divider,
  Chip,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  LinearProgress,
  Avatar,
  Badge,
  IconButton,
  FormHelperText
} from '@mui/material';
import { 
  School, 
  NavigateNext, 
  NavigateBefore, 
  Person,
  Phone,
  Email,
  LocationOn,
  ContactPhone,
  FamilyRestroom,
  Preview,
  Payment,
  Send,
  CheckCircle,
  CheckCircleOutline,
  CreditCard,
  AccountBalance,
  PhoneAndroid,
  CloudUpload,
  AttachFile,
  PhotoCamera,
  Delete,
  Print,
  GetApp,
  Info,
  Assignment
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import axios from 'axios';

const AdmissionPage = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [submissionCompleted, setSubmissionCompleted] = useState(false);
  const [applicationId, setApplicationId] = useState(null);
  const [trackingId, setTrackingId] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [showThankYou, setShowThankYou] = useState(false);
  
  // Document upload states
  const [documentsFile, setDocumentsFile] = useState(null);
  const [profilePicture, setProfilePicture] = useState(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  
  // Application fee configuration
  const APPLICATION_FEE = 500; // Fee in your currency
  const [schoolInfo, setSchoolInfo] = useState({
    school_name: 'EMIS School',
    school_logo: null
  });
  const [formData, setFormData] = useState({
    // Personal Information
    first_name: '',
    last_name: '',
    date_of_birth: '',
    gender: '',
    class: '',
    blood_group: '',
    religion: '',
    nationality: 'Nepali',
    applied_class: '',
    previous_school: '',
    previous_grade: '',
    previous_percentage: '',
    
    // Contact Information
    phone: '',
    email: '',
    address: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'Nepal',
    
    // Parent Information
    father_name: '',
    father_occupation: '',
    father_phone: '',
    father_email: '',
    mother_name: '',
    mother_occupation: '',
    mother_phone: '',
    mother_email: '',
    guardian_name: '',
    guardian_relation: '',
    guardian_phone: '',
    guardian_email: '',
    guardian_address: '',
  });

  const steps = [
    { label: 'Personal Information', icon: <Person />, description: 'Basic student details' },
    { label: 'Contact Details', icon: <ContactPhone />, description: 'Address and communication' },
    { label: 'Parent Information', icon: <FamilyRestroom />, description: 'Guardian details' },
    { label: 'Documents Upload', icon: <CloudUpload />, description: 'Upload required documents' },
    { label: 'Review Application', icon: <Preview />, description: 'Verify all information' },
    { label: 'Payment', icon: <Payment />, description: 'Application fee payment' },
    { label: 'Submit', icon: <Send />, description: 'Final submission' }
  ];
  
  const classes = ['Nursery', 'LKG', 'UKG', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
  const bloodGroups = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
  const genders = ['male', 'female', 'other'];
  const paymentMethods = [
    { value: 'credit_card', label: 'Credit/Debit Card', icon: <CreditCard /> },
    { value: 'net_banking', label: 'Net Banking', icon: <AccountBalance /> },
    { value: 'upi', label: 'UPI Payment', icon: <PhoneAndroid /> }
  ];

  // Load saved form data from localStorage on component mount
  useEffect(() => {
    const savedData = localStorage.getItem('admissionFormData');
    if (savedData) {
      setFormData(JSON.parse(savedData));
    }
    const savedStep = localStorage.getItem('admissionFormStep');
    if (savedStep) {
      setActiveStep(parseInt(savedStep));
    }
  }, []);

  // Save form data to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('admissionFormData', JSON.stringify(formData));
  }, [formData]);

  // Save current step to localStorage
  useEffect(() => {
    localStorage.setItem('admissionFormStep', activeStep.toString());
  }, [activeStep]);

  // Fetch school info on component mount
  useEffect(() => {
    const fetchSchoolInfo = async () => {
      try {
        const response = await axios.get('/api/settings/school-info');
        if (response.data.success) {
          setSchoolInfo({
            school_name: response.data.data.school_name || 'EMIS School',
            school_logo: response.data.data.school_logo
          });
        }
      } catch (error) {
        console.error('Error fetching school info:', error);
        // Keep default values on error
      }
    };

    fetchSchoolInfo();
  }, []);

  // File upload handlers
  const handleProfilePictureUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        enqueueSnackbar('Profile picture must be less than 5MB', { variant: 'error' });
        return;
      }
      if (!file.type.startsWith('image/')) {
        enqueueSnackbar('Please upload a valid image file', { variant: 'error' });
        return;
      }
      
      setProfilePicture(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfilePicturePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDocumentsUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        enqueueSnackbar('Documents file must be less than 10MB', { variant: 'error' });
        return;
      }
      if (file.type !== 'application/pdf') {
        enqueueSnackbar('Please upload documents as a single PDF file', { variant: 'error' });
        return;
      }
      
      setDocumentsFile(file);
      enqueueSnackbar('Documents uploaded successfully', { variant: 'success' });
    }
  };

  const removeProfilePicture = () => {
    setProfilePicture(null);
    setProfilePicturePreview(null);
  };

  const removeDocuments = () => {
    setDocumentsFile(null);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const validateStep = (step) => {
    switch (step) {
      case 0: // Personal Information
        return formData.first_name && formData.last_name && formData.date_of_birth && 
               formData.gender && formData.class;
      case 1: // Contact Details
        return formData.phone && formData.email && formData.address && formData.city;
      case 2: // Parent Information
        return formData.father_name && formData.mother_name;
      case 3: // Document Upload
        return profilePicture && documentsFile; // Both files are required
      case 4: // Review Application
        return true; // Review step doesn't need validation
      case 5: // Payment
        return paymentCompleted;
      case 6: // Submit
        return paymentCompleted;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    } else {
      enqueueSnackbar('Please fill in all required fields', { variant: 'warning' });
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  // Tracking ID is now generated by the backend

  const handlePayment = async (selectedPaymentMethod) => {
    setProcessingPayment(true);
    setPaymentMethod(selectedPaymentMethod);
    
    try {
      // Simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      // In a real application, you would integrate with a payment gateway here
      // For demo purposes, we'll simulate successful payment
      setPaymentCompleted(true);
      localStorage.setItem('admissionPaymentStatus', 'completed');
      localStorage.setItem('admissionPaymentMethod', selectedPaymentMethod);
      
      enqueueSnackbar(`Payment completed successfully via ${selectedPaymentMethod}!`, { variant: 'success' });
      
      // Auto-advance to final step after payment
      setTimeout(() => {
        setActiveStep(5);
      }, 1500);
      
    } catch (error) {
      console.error('Payment error:', error);
      enqueueSnackbar('Payment processing failed. Please try again.', { variant: 'error' });
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    if (!paymentCompleted) {
      enqueueSnackbar('Please complete payment before submitting', { variant: 'warning' });
      return;
    }
    
    setLoading(true);

    try {
      // Include payment status info in form data (tracking ID will be generated by backend)
      const submissionData = {
        ...formData,
        payment_status: 'completed',
        payment_method: paymentMethod,
        application_fee: APPLICATION_FEE,
        payment_date: new Date().toISOString(),
        submission_date: new Date().toISOString()
      };
      
      // Create FormData for file upload
      const formDataToSubmit = new FormData();
      
      // Add all form fields with proper mapping
      Object.keys(formData).forEach(key => {
        if (formData[key]) {
          // Map 'class' to 'applied_class' for backend compatibility
          const backendKey = key === 'class' ? 'applied_class' : key;
          formDataToSubmit.append(backendKey, formData[key]);
        }
      });
      
      // Add payment information
      formDataToSubmit.append('payment_status', 'completed');
      formDataToSubmit.append('payment_method', paymentMethod || 'online');
      formDataToSubmit.append('application_fee', APPLICATION_FEE);
      formDataToSubmit.append('payment_date', new Date().toISOString());
      // Note: tracking_id will be generated by backend
      
      // Add missing fields with default values
      formDataToSubmit.append('father_income', '');
      formDataToSubmit.append('mother_income', '');
      formDataToSubmit.append('medical_conditions', '');
      formDataToSubmit.append('special_requirements', '');
      formDataToSubmit.append('emergency_contact_name', formData.father_name || '');
      formDataToSubmit.append('emergency_contact_phone', formData.father_phone || '');
      formDataToSubmit.append('emergency_contact_relation', 'Father');
      
      // Add files
      if (profilePicture) {
        formDataToSubmit.append('profile_picture', profilePicture);
      }
      if (documentsFile) {
        formDataToSubmit.append('documents', documentsFile);
      }
      


      // Debug: Log form data being sent
      console.log('Form data being submitted:');
      for (let [key, value] of formDataToSubmit.entries()) {
        console.log(`${key}:`, value);
      }
      
      const response = await axios.post('/api/enhanced-admissions/apply', formDataToSubmit, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });
      
      console.log('Backend response:', response.data);
      
      if (response.data && response.data.success) {
        // Set tracking ID and application ID from backend response
        if (response.data.tracking_id) {
          setTrackingId(response.data.tracking_id);
          console.log('Tracking ID set from backend:', response.data.tracking_id);
        }
        if (response.data.application_id) {
          setApplicationId(response.data.application_id);
        }
        
        setSubmissionCompleted(true);
        setShowThankYou(true);
        enqueueSnackbar('Admission application submitted successfully!', { variant: 'success' });
      } else {
        throw new Error(response.data?.message || 'Submission failed');
      }
      
      // Clear saved form data after successful submission
      localStorage.removeItem('admissionFormData');
      localStorage.removeItem('admissionFormStep');
      localStorage.removeItem('admissionPaymentStatus');
      localStorage.removeItem('admissionPaymentMethod');
      
    } catch (error) {
      console.error('Admission error:', error);
      const errorMessage = error.response?.data?.message || 'Error submitting application. Please try again.';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handlePrintApplication = () => {
    console.log('Print function called');
    
    // Check if tracking ID is available
    if (!trackingId) {
      enqueueSnackbar('No tracking ID available. Please ensure your application is submitted.', { variant: 'error' });
      return;
    }
    
    // Wait a moment for DOM to be ready
    setTimeout(() => {
      const applicationDetails = document.getElementById('application-details-print');
      console.log('Print element found:', !!applicationDetails);
      
      if (!applicationDetails) {
        enqueueSnackbar('Application details not found. Please ensure your application is submitted.', { variant: 'error' });
        return;
      }
      
      // Check if element has content
      if (!applicationDetails.innerHTML.trim()) {
        enqueueSnackbar('Application details are empty. Please try again.', { variant: 'error' });
        return;
      }

      // Create a new window with only the application details
      const printWindow = window.open('', '_blank');
    
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Admission Application - ${formData.first_name} ${formData.last_name}</title>
            <style>
              @media print {
                body { margin: 0; padding: 20px; font-family: Arial, sans-serif; }
                .print-container { max-width: 800px; margin: 0 auto; }
                .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 20px; }
                .section { margin-bottom: 25px; }
                .section-title { font-size: 18px; font-weight: bold; color: #333; margin-bottom: 15px; border-bottom: 1px solid #ccc; padding-bottom: 5px; }
                .detail-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 15px; }
                .detail-item { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px dotted #ccc; }
                .detail-label { font-weight: bold; color: #555; }
                .detail-value { color: #333; }
                .status-box { background: #e8f5e8; border: 2px solid #4caf50; padding: 15px; text-align: center; margin: 20px 0; }
                .tracking-id { font-size: 24px; font-weight: bold; color: #2196f3; letter-spacing: 2px; }
              }
              @page { size: A4; margin: 1in; }
            </style>
          </head>
          <body>
            ${applicationDetails.innerHTML}
          </body>
        </html>
      `);
      printWindow.document.close();
      
      // Wait for content to load before printing
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 500);
      } else {
        enqueueSnackbar('Unable to open print window', { variant: 'error' });
      }
    }, 100); // Close the setTimeout
  };

  const handleDownloadReceipt = async () => {
    try {
      console.log('Starting receipt download for tracking ID:', trackingId);
      console.log('Form data:', { 
        firstName: formData.first_name, 
        lastName: formData.last_name, 
        class: formData.applied_class || formData.class,
        email: formData.email,
        phone: formData.phone
      });
      
      // Validate required data
      if (!trackingId || !formData.first_name || !formData.last_name) {
        enqueueSnackbar('Missing required information for receipt generation', { variant: 'error' });
        return;
      }

      // First verify the tracking ID exists
      console.log('Verifying tracking ID exists...');
      const verifyResponse = await axios.get(`/api/enhanced-admissions/track/${trackingId}`);
      if (!verifyResponse.data.success) {
        enqueueSnackbar('Invalid tracking ID. Please check your tracking ID and try again.', { variant: 'error' });
        return;
      }
      console.log('Tracking ID verified successfully');

      // Call backend API to generate PDF receipt
      const response = await axios.post('/api/enhanced-admissions/receipt', {
        trackingId,
        applicationId,
        studentName: `${formData.first_name} ${formData.last_name}`,
        class: formData.applied_class || formData.class,
        applicationFee: APPLICATION_FEE,
        paymentMethod,
        submissionDate: new Date().toLocaleDateString(),
        studentDetails: {
          email: formData.email,
          phone: formData.phone
        }
      }, {
        responseType: 'blob',
        headers: {
          'Accept': 'application/pdf'
        }
      });
      
      console.log('Receipt API response:', response.status, response.headers);
      
      // Check if response is actually a PDF
      if (response.data.type && !response.data.type.includes('pdf')) {
        console.error('Response is not a PDF:', response.data.type);
        enqueueSnackbar('Invalid file format received', { variant: 'error' });
        return;
      }
      
      // Create blob link to download PDF
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `admission_receipt_${trackingId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      enqueueSnackbar('Receipt downloaded successfully', { variant: 'success' });
    } catch (error) {
      console.error('Download receipt error:', error);
      if (error.response) {
        console.error('Error response:', error.response.status, error.response.data);
        enqueueSnackbar(`Failed to download receipt: ${error.response.status}`, { variant: 'error' });
      } else {
        enqueueSnackbar('Failed to download receipt', { variant: 'error' });
      }
    }
  };

  const handleTrackApplication = () => {
    // Navigate to track application page with tracking ID
    navigate(`/track-application?id=${trackingId}`);
  };

  const getStepContent = (step) => {
    if (showThankYou) {
      return (
        <Card sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}>
          <CardContent sx={{ textAlign: 'center', p: 4 }}>
            <CheckCircleOutline sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom color="success.main">
              Application Submitted Successfully!
            </Typography>
            <Typography variant="h6" gutterBottom>
              Thank you, {formData.first_name} {formData.last_name}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
              Your admission application has been submitted successfully. Please keep your tracking ID for future reference.
            </Typography>
            
            <Card sx={{ mb: 3, bgcolor: 'primary.50', border: '2px solid', borderColor: 'primary.main' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary.main">
                  Application Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Tracking ID:</Typography>
                    <Typography variant="h6" color="primary.main">{trackingId}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Application ID:</Typography>
                    <Typography variant="h6">{applicationId || 'Pending'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Class:</Typography>
                    <Typography variant="body1">{formData.class}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Application Fee:</Typography>
                    <Typography variant="body1">₹{APPLICATION_FEE}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Payment Method:</Typography>
                    <Typography variant="body1">{paymentMethod.replace('_', ' ').toUpperCase()}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Submission Date:</Typography>
                    <Typography variant="body1">{new Date().toLocaleDateString()}</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
            
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<Print />}
                onClick={handlePrintApplication}
                sx={{ minWidth: 150 }}
              >
                Print Details
              </Button>
              <Button
                variant="outlined"
                startIcon={<GetApp />}
                onClick={handleDownloadReceipt}
                sx={{ minWidth: 150 }}
              >
                Download Receipt
              </Button>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<Info />}
                onClick={handleTrackApplication}
                sx={{ minWidth: 150 }}
              >
                Track Application
              </Button>
              <Button
                variant="text"
                onClick={() => navigate('/')}
                sx={{ minWidth: 150 }}
              >
                Back to Home
              </Button>
            </Box>
            
            {/* Hidden printable content */}
            <div id="application-details-print" style={{ display: 'none' }}>
              <div className="print-container">
                <div className="header">
                  <h1>EMIS SCHOOL</h1>
                  <h2>Admission Application Receipt</h2>
                  <p>Education Management Information System</p>
                </div>
                
                <div className="status-box">
                  <h3>Application Submitted Successfully!</h3>
                  <div className="tracking-id">{trackingId}</div>
                  <p>Please keep this tracking ID for future reference</p>
                </div>
                
                <div className="section">
                  <div className="section-title">Personal Information</div>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">Full Name:</span>
                      <span className="detail-value">{formData.first_name} {formData.last_name}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Date of Birth:</span>
                      <span className="detail-value">{formData.date_of_birth}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Gender:</span>
                      <span className="detail-value">{formData.gender}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Class:</span>
                      <span className="detail-value">{formData.class}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Blood Group:</span>
                      <span className="detail-value">{formData.blood_group}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Religion:</span>
                      <span className="detail-value">{formData.religion}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Nationality:</span>
                      <span className="detail-value">{formData.nationality}</span>
                    </div>
                  </div>
                </div>
                
                <div className="section">
                  <div className="section-title">Contact Information</div>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">Phone:</span>
                      <span className="detail-value">{formData.phone}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Email:</span>
                      <span className="detail-value">{formData.email}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Address:</span>
                      <span className="detail-value">{formData.address}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">City:</span>
                      <span className="detail-value">{formData.city}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">State:</span>
                      <span className="detail-value">{formData.state}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Country:</span>
                      <span className="detail-value">{formData.country}</span>
                    </div>
                  </div>
                </div>
                
                <div className="section">
                  <div className="section-title">Parent Information</div>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">Father's Name:</span>
                      <span className="detail-value">{formData.father_name}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Father's Phone:</span>
                      <span className="detail-value">{formData.father_phone}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Mother's Name:</span>
                      <span className="detail-value">{formData.mother_name}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Mother's Phone:</span>
                      <span className="detail-value">{formData.mother_phone}</span>
                    </div>
                  </div>
                </div>
                
                <div className="section">
                  <div className="section-title">Payment Information</div>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">Application Fee:</span>
                      <span className="detail-value">₹{APPLICATION_FEE}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Payment Method:</span>
                      <span className="detail-value">{paymentMethod.replace('_', ' ').toUpperCase()}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Payment Status:</span>
                      <span className="detail-value">COMPLETED</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Submission Date:</span>
                      <span className="detail-value">{new Date().toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
                
                <div className="section">
                  <div className="section-title">Next Steps</div>
                  <ul>
                    <li>You will receive an email confirmation shortly</li>
                    <li>Application review process takes 3-5 business days</li>
                    <li>You can track your application status using the tracking ID</li>
                    <li>Contact us at admissions@emis.edu for any queries</li>
                  </ul>
                </div>
              </div>
            </div>
            
            <Alert severity="info" sx={{ mt: 3, textAlign: 'left' }}>
              <Typography variant="body2">
                <strong>Next Steps:</strong><br/>
                • You will receive an email confirmation shortly<br/>
                • Application review process takes 3-5 business days<br/>
                • You can track your application status using the tracking ID<br/>
                • Contact us at admissions@emis.edu for any queries
              </Typography>
            </Alert>
          </CardContent>
        </Card>
      );
    }

    switch (step) {
      case 0:
        return (
          <Card>
            <CardHeader 
              title="Personal Information" 
              avatar={<Person color="primary" />}
              subheader="Please provide basic student details"
            />
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="first_name"
                    label="First Name"
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="last_name"
                    label="Last Name"
                    value={formData.last_name}
                    onChange={handleChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="date_of_birth"
                    label="Date of Birth"
                    type="date"
                    value={formData.date_of_birth}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Gender</InputLabel>
                    <Select
                      name="gender"
                      value={formData.gender}
                      label="Gender"
                      onChange={handleChange}
                    >
                      {genders.map((gender) => (
                        <MenuItem key={gender} value={gender}>
                          {gender.charAt(0).toUpperCase() + gender.slice(1)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Blood Group</InputLabel>
                    <Select
                      name="blood_group"
                      value={formData.blood_group}
                      label="Blood Group"
                      onChange={handleChange}
                    >
                      {bloodGroups.map((group) => (
                        <MenuItem key={group} value={group}>{group}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="religion"
                    label="Religion"
                    value={formData.religion}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="nationality"
                    label="Nationality"
                    value={formData.nationality}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Class Applying For</InputLabel>
                    <Select
                      name="class"
                      value={formData.class}
                      label="Class Applying For"
                      onChange={handleChange}
                    >
                      {classes.map((cls) => (
                        <MenuItem key={cls} value={cls}>{cls}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        );
      case 1:
        return (
          <Card>
            <CardHeader 
              title="Contact Information" 
              avatar={<ContactPhone color="primary" />}
              subheader="Address and communication details"
            />
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="phone"
                    label="Phone Number"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    InputProps={{
                      startAdornment: <Phone sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="email"
                    label="Email Address"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    InputProps={{
                      startAdornment: <Email sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="address"
                    label="Address"
                    multiline
                    rows={3}
                    value={formData.address}
                    onChange={handleChange}
                    required
                    InputProps={{
                      startAdornment: <LocationOn sx={{ mr: 1, color: 'text.secondary', alignSelf: 'flex-start', mt: 1 }} />
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="city"
                    label="City"
                    value={formData.city}
                    onChange={handleChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="state"
                    label="State/Province"
                    value={formData.state}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="postal_code"
                    label="Postal Code"
                    value={formData.postal_code}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="country"
                    label="Country"
                    value={formData.country}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        );
      case 2:
        return (
          <Card>
            <CardHeader 
              title="Parent/Guardian Information" 
              avatar={<FamilyRestroom color="primary" />}
              subheader="Family and guardian contact details"
            />
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
                    Father's Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="father_name"
                    label="Father's Name"
                    value={formData.father_name}
                    onChange={handleChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="father_occupation"
                    label="Father's Occupation"
                    value={formData.father_occupation}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="father_phone"
                    label="Father's Phone"
                    value={formData.father_phone}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="father_email"
                    label="Father's Email"
                    type="email"
                    value={formData.father_email}
                    onChange={handleChange}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ mb: 2, mt: 2, fontWeight: 'bold', color: 'primary.main' }}>
                    Mother's Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="mother_name"
                    label="Mother's Name"
                    value={formData.mother_name}
                    onChange={handleChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="mother_occupation"
                    label="Mother's Occupation"
                    value={formData.mother_occupation}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="mother_phone"
                    label="Mother's Phone"
                    value={formData.mother_phone}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="mother_email"
                    label="Mother's Email"
                    type="email"
                    value={formData.mother_email}
                    onChange={handleChange}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ mb: 2, mt: 2, fontWeight: 'bold', color: 'primary.main' }}>
                    Guardian Information (if different from parents)
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="guardian_name"
                    label="Guardian Name"
                    value={formData.guardian_name}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="guardian_relation"
                    label="Relation to Student"
                    value={formData.guardian_relation}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="guardian_phone"
                    label="Guardian Phone"
                    value={formData.guardian_phone}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="guardian_email"
                    label="Guardian Email"
                    type="email"
                    value={formData.guardian_email}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="guardian_address"
                    label="Guardian Address"
                    multiline
                    rows={2}
                    value={formData.guardian_address}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        );
      case 3:
        return (
          <Card>
            <CardHeader 
              title="Document Upload" 
              avatar={<CloudUpload color="primary" />}
              subheader="Upload required documents"
            />
            <CardContent>
              <Alert severity="info" sx={{ mb: 3 }}>
                Please upload the required documents. Both profile picture and documents are mandatory.
              </Alert>
              
              <Grid container spacing={3}>
                {/* Profile Picture Upload */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ p: 2, border: '2px dashed', borderColor: 'primary.main', bgcolor: 'primary.50' }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <PhotoCamera sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                      <Typography variant="h6" gutterBottom>
                        Profile Picture
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Upload a recent passport-size photograph
                      </Typography>
                      
                      {profilePicturePreview ? (
                        <Box sx={{ mb: 2 }}>
                          <img 
                            src={profilePicturePreview} 
                            alt="Profile Preview" 
                            style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: '8px' }}
                          />
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" color="success.main">
                              ✓ {profilePicture?.name}
                            </Typography>
                            <Button 
                              size="small" 
                              color="error" 
                              startIcon={<Delete />}
                              onClick={removeProfilePicture}
                              sx={{ mt: 1 }}
                            >
                              Remove
                            </Button>
                          </Box>
                        </Box>
                      ) : (
                        <Button
                          variant="outlined"
                          component="label"
                          startIcon={<PhotoCamera />}
                          sx={{ mb: 2 }}
                        >
                          Choose Photo
                          <input
                            type="file"
                            hidden
                            accept="image/*"
                            onChange={handleProfilePictureUpload}
                          />
                        </Button>
                      )}
                      
                      <Typography variant="caption" display="block" color="text.secondary">
                        Max size: 5MB • Formats: JPG, PNG
                      </Typography>
                    </Box>
                  </Card>
                </Grid>
                
                {/* Documents Upload */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ p: 2, border: '2px dashed', borderColor: 'secondary.main', bgcolor: 'secondary.50' }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Assignment sx={{ fontSize: 48, color: 'secondary.main', mb: 2 }} />
                      <Typography variant="h6" gutterBottom>
                        Academic Documents
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Upload all required documents as a single PDF
                      </Typography>
                      
                      {documentsFile ? (
                        <Box sx={{ mb: 2 }}>
                          <AttachFile sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                          <Typography variant="body2" color="success.main">
                            ✓ {documentsFile.name}
                          </Typography>
                          <Typography variant="caption" display="block" color="text.secondary">
                            Size: {(documentsFile.size / (1024 * 1024)).toFixed(2)} MB
                          </Typography>
                          <Button 
                            size="small" 
                            color="error" 
                            startIcon={<Delete />}
                            onClick={removeDocuments}
                            sx={{ mt: 1 }}
                          >
                            Remove
                          </Button>
                        </Box>
                      ) : (
                        <Button
                          variant="outlined"
                          component="label"
                          startIcon={<CloudUpload />}
                          sx={{ mb: 2 }}
                        >
                          Upload Documents
                          <input
                            type="file"
                            hidden
                            accept=".pdf"
                            onChange={handleDocumentsUpload}
                          />
                        </Button>
                      )}
                      
                      <Typography variant="caption" display="block" color="text.secondary">
                        Max size: 10MB • Format: PDF only
                      </Typography>
                    </Box>
                  </Card>
                </Grid>
              </Grid>
              
              {/* Document Requirements */}
              <Alert severity="warning" sx={{ mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Required Documents (combine all into one PDF):
                </Typography>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  <li>Birth Certificate</li>
                  <li>Previous School Report Card/Transcript</li>
                  <li>Transfer Certificate (if applicable)</li>
                  <li>Parent/Guardian ID Copy</li>
                  <li>Address Proof</li>
                </ul>
              </Alert>
            </CardContent>
          </Card>
        );
      case 4:
        return (
          <Card>
            <CardHeader 
              title="Review Application" 
              avatar={<Preview color="primary" />}
              subheader="Please review all information before proceeding"
            />
            <CardContent>
              <Alert severity="info" sx={{ mb: 3 }}>
                Please carefully review all the information you have provided. You can go back to make changes if needed.
              </Alert>
              
              {/* Personal Information Review */}
              <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', display: 'flex', alignItems: 'center' }}>
                    <Person sx={{ mr: 1 }} /> Personal Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography><strong>Name:</strong> {formData.first_name} {formData.last_name}</Typography>
                      <Typography><strong>Date of Birth:</strong> {formData.date_of_birth}</Typography>
                      <Typography><strong>Gender:</strong> {formData.gender}</Typography>
                      <Typography><strong>Class:</strong> {formData.class}</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography><strong>Blood Group:</strong> {formData.blood_group || 'Not specified'}</Typography>
                      <Typography><strong>Religion:</strong> {formData.religion || 'Not specified'}</Typography>
                      <Typography><strong>Nationality:</strong> {formData.nationality || 'Not specified'}</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
              
              {/* Contact Information Review */}
              <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', display: 'flex', alignItems: 'center' }}>
                    <ContactPhone sx={{ mr: 1 }} /> Contact Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography><strong>Phone:</strong> {formData.phone}</Typography>
                      <Typography><strong>Email:</strong> {formData.email}</Typography>
                      <Typography><strong>Address:</strong> {formData.address}</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography><strong>City:</strong> {formData.city}</Typography>
                      <Typography><strong>State:</strong> {formData.state || 'Not specified'}</Typography>
                      <Typography><strong>Country:</strong> {formData.country || 'Not specified'}</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
              
              {/* Parent Information Review */}
              <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', display: 'flex', alignItems: 'center' }}>
                    <FamilyRestroom sx={{ mr: 1 }} /> Parent/Guardian Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography><strong>Father's Name:</strong> {formData.father_name}</Typography>
                      <Typography><strong>Father's Occupation:</strong> {formData.father_occupation || 'Not specified'}</Typography>
                      <Typography><strong>Father's Phone:</strong> {formData.father_phone || 'Not specified'}</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography><strong>Mother's Name:</strong> {formData.mother_name}</Typography>
                      <Typography><strong>Mother's Occupation:</strong> {formData.mother_occupation || 'Not specified'}</Typography>
                      <Typography><strong>Mother's Phone:</strong> {formData.mother_phone || 'Not specified'}</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
              
              {/* Documents Review */}
              <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', display: 'flex', alignItems: 'center' }}>
                    <CloudUpload sx={{ mr: 1 }} /> Uploaded Documents
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography><strong>Profile Picture:</strong> {profilePicture ? `✓ ${profilePicture.name}` : '❌ Not uploaded'}</Typography>
                      <Typography><strong>Academic Documents:</strong> {documentsFile ? `✓ ${documentsFile.name}` : '❌ Not uploaded'}</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      {profilePicture && (
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>Profile Picture Preview:</Typography>
                          <img 
                            src={profilePicturePreview} 
                            alt="Profile Preview" 
                            style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: '8px', border: '2px solid #ddd' }}
                          />
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </CardContent>
          </Card>
        );
      case 5:
        return (
          <Card>
            <CardHeader 
              title="Payment" 
              avatar={<Payment color="primary" />}
              subheader="Complete application fee payment"
            />
            <CardContent>
              <Alert severity="info" sx={{ mb: 3 }}>
                Please complete the application processing fee payment to proceed with your admission application.
              </Alert>
              
              {/* Fee Details */}
              <Card sx={{ mb: 3, border: '2px solid', borderColor: 'primary.main' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                    Application Processing Fee
                  </Typography>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      ₹{APPLICATION_FEE}
                    </Typography>
                    {paymentCompleted && (
                      <Chip 
                        label="Payment Completed" 
                        color="success" 
                        icon={<CheckCircle />}
                        sx={{ fontSize: '1rem', py: 2 }}
                      />
                    )}
                  </Box>
                  
                  <Alert severity="warning" sx={{ mb: 3 }}>
                    This fee covers application processing, document verification, and administrative costs. This fee is non-refundable.
                  </Alert>
                  
                  {!paymentCompleted ? (
                    <>
                      <Typography variant="subtitle1" sx={{ mb: 3, fontWeight: 'bold' }}>
                        Select Payment Method:
                      </Typography>
                      
                      <Grid container spacing={2}>
                        {paymentMethods.map((method) => (
                          <Grid item xs={12} sm={6} md={4} key={method.value}>
                            <Button
                              fullWidth
                              variant={method.value === 'credit_card' ? 'contained' : 'outlined'}
                              size="large"
                              onClick={() => handlePayment(method.value)}
                              disabled={processingPayment}
                              startIcon={method.icon}
                              sx={{ py: 2, minHeight: 60 }}
                            >
                              {processingPayment ? (
                                <CircularProgress size={20} />
                              ) : (
                                method.label
                              )}
                            </Button>
                          </Grid>
                        ))}
                      </Grid>
                      
                      {processingPayment && (
                        <Box sx={{ mt: 3, textAlign: 'center' }}>
                          <Typography variant="h6" gutterBottom>
                            Processing Payment...
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Please wait while we process your payment. Do not refresh the page.
                          </Typography>
                          <LinearProgress sx={{ mt: 1 }} />
                        </Box>
                      )}
                    </>
                  ) : (
                    <Alert severity="success" icon={<CheckCircle />}>
                      <Typography variant="h6">Payment Successful!</Typography>
                      <Typography>Your payment has been processed successfully. You can now proceed to submit your application.</Typography>
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </CardContent>
          </Card>
        );
        
      case 6:
        return (
          <Card>
            <CardHeader 
              title="Submit Application" 
              avatar={<Send color="primary" />}
              subheader="Final submission of your admission application"
            />
            <CardContent>
              {paymentCompleted ? (
                <>
                  <Alert severity="success" sx={{ mb: 3 }}>
                    Payment completed successfully! Your application is ready for final submission.
                  </Alert>
                  
                  <Card sx={{ mb: 3, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.main' }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2, color: 'success.main' }}>
                        Final Review
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography><strong>Applicant:</strong> {formData.first_name} {formData.last_name}</Typography>
                          <Typography><strong>Class:</strong> {formData.class}</Typography>
                          <Typography><strong>Email:</strong> {formData.email}</Typography>
                          <Typography><strong>Phone:</strong> {formData.phone}</Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography><strong>Processing Fee:</strong> ₹{APPLICATION_FEE}</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography component="span"><strong>Payment Status:</strong></Typography>
                            <Chip label="Completed" color="success" size="small" />
                          </Box>
                          <Typography><strong>Payment Method:</strong> {paymentMethod.replace('_', ' ').toUpperCase()}</Typography>
                          <Typography><strong>Application Date:</strong> {new Date().toLocaleDateString()}</Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                  
                  <Alert severity="info" sx={{ mb: 3 }}>
                    By clicking "Submit Application", you confirm that all information provided is accurate and complete.
                  </Alert>
                  
                  <Box sx={{ textAlign: 'center' }}>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={handleSubmit}
                      disabled={loading}
                      startIcon={loading ? <CircularProgress size={20} /> : <Send />}
                      sx={{ px: 6, py: 2, fontSize: '1.1rem' }}
                    >
                      {loading ? 'Submitting Application...' : 'Submit Application'}
                    </Button>
                  </Box>
                </>
              ) : (
                <Alert severity="error">
                  Please complete the payment process before submitting your application.
                </Alert>
              )}
            </CardContent>
          </Card>
        );
      default:
        return 'Unknown step';
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5', py: 4 }}>
      <Container maxWidth="lg">
        <Paper sx={{ p: 4 }}>
          <Box textAlign="center" mb={4}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2
              }}
            >
              {schoolInfo.school_logo ? (
                <img
                  src={`http://localhost:3000${schoolInfo.school_logo}`}
                  alt="School Logo"
                  style={{
                    height: 48,
                    width: 48,
                    objectFit: 'contain',
                    marginRight: 8
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
              ) : null}
              <School 
                sx={{ 
                  fontSize: 48, 
                  color: 'primary.main',
                  display: schoolInfo.school_logo ? 'none' : 'block'
                }} 
              />
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
              {schoolInfo.school_name} Admission Application
            </Typography>
            <Typography color="text.secondary">
              Complete your application in simple steps
            </Typography>
          </Box>

          {!showThankYou && (
            <>
              {/* Enhanced Stepper with Icons */}
              <Stepper activeStep={activeStep} sx={{ mb: 4 }} alternativeLabel>
                {steps.map((step, index) => (
                  <Step key={step.label} completed={index < activeStep}>
                    <StepLabel 
                      icon={step.icon}
                      sx={{
                        '& .MuiStepLabel-label': {
                          fontSize: '0.9rem',
                          fontWeight: index === activeStep ? 'bold' : 'normal'
                        }
                      }}
                    >
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'inherit' }}>
                          {step.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {step.description}
                        </Typography>
                      </Box>
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>

              {/* Progress Indicator */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Step {activeStep + 1} of {steps.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {Math.round(((activeStep + 1) / steps.length) * 100)}% Complete
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={((activeStep + 1) / steps.length) * 100}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
            </>
          )}

          {/* Step Content */}
          {getStepContent(activeStep)}

          {/* Navigation Buttons */}
          {!showThankYou && (
            <Box mt={4} display="flex" justifyContent="space-between">
              <Button 
                onClick={() => navigate('/')}
                disabled={loading || processingPayment}
                startIcon={<School />}
                variant="text"
              >
                Back to Home
              </Button>
              
              <Box>
                {activeStep !== 0 && (
                  <Button 
                    onClick={handleBack} 
                    sx={{ mr: 2 }}
                    startIcon={<NavigateBefore />}
                    disabled={loading || processingPayment}
                    variant="outlined"
                  >
                    Back
                  </Button>
                )}
                
                {activeStep === steps.length - 1 ? (
                  // Final step - handled within the step content
                  null
                ) : activeStep === 5 ? (
                  // Payment step - show "Proceed to Submit" after payment
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    endIcon={<NavigateNext />}
                    disabled={loading || !paymentCompleted || processingPayment}
                    sx={{ px: 4 }}
                  >
                    {paymentCompleted ? 'Proceed to Submit' : 'Complete Payment First'}
                  </Button>
                ) : (
                  // Regular steps - Next button
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    endIcon={<NavigateNext />}
                    disabled={loading || processingPayment}
                    sx={{ px: 4 }}
                  >
                    Next
                  </Button>
                )}
              </Box>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default AdmissionPage;
